import { customElement, attachShadowTemplate, coreProperty, coerceBoolean } from '@tylertech/forge-core';
import { IconAdapter } from './icon-adapter';
import { IconCore } from './icon-core';
import { ICON_CONSTANTS, IconUrlBuilder, IconExternalType, IconTheme } from './icon-constants';
import { BaseComponent, IBaseComponent } from '../core/base/base-component';

const template = '<template></template>';
const styles = ':host{--_icon-color:var(--forge-icon-color, currentColor);--_icon-size:var(--forge-icon-size, 1em);--_icon-width:var(--forge-icon-width, var(--_icon-size));--_icon-height:var(--forge-icon-height, var(--_icon-size));--_icon-font-size:var(--forge-icon-font-size, calc(var(--forge-typography-font-size, 1rem) * 1.5))}:host{display:inline-block;-webkit-box-sizing:content-box;box-sizing:content-box;contain:content;font-size:var(--_icon-font-size)}:host([hidden]){display:none}svg{display:block;width:var(--_icon-width);height:var(--_icon-height);fill:var(--_icon-color);stroke:var(--_icon-color);stroke-width:0}:host([theme=primary]){--_icon-color:var(--forge-icon-color, var(--forge-theme-primary, #3f51b5))}:host([theme=secondary]){--_icon-color:var(--forge-icon-color, var(--forge-theme-secondary, #ffc107))}:host([theme=tertiary]){--_icon-color:var(--forge-icon-color, var(--forge-theme-tertiary, #3d5afe))}:host([theme=success]){--_icon-color:var(--forge-icon-color, var(--forge-theme-success, #2e7d32))}:host([theme=error]){--_icon-color:var(--forge-icon-color, var(--forge-theme-error, #b00020))}:host([theme=warning]){--_icon-color:var(--forge-icon-color, var(--forge-theme-warning, #d14900))}:host([theme=text-medium]){--_icon-color:var(--forge-icon-color, var(--forge-theme-text-medium, rgba(0, 0, 0, 0.6)))}:host([theme=text-low]){--_icon-color:var(--forge-icon-color, var(--forge-theme-text-low, rgba(0, 0, 0, 0.38)))}';

export interface IIconProperties {
  name: string | undefined;
  src: string | undefined;
  lazy: boolean;
  external: boolean;
  externalType: IconExternalType;
  externalUrlBuilder: IconUrlBuilder;
  theme: IconTheme;
  viewbox: string;
}

export interface IIconComponent extends IIconProperties, IBaseComponent {
  layout(): void;
}

declare global {
  interface HTMLElementTagNameMap {
    'forge-icon': IIconComponent;
  }
}

/**
 * @tag forge-icon
 *
 * @summary Icons are used to represent information visually
 *
 * @property {string} name - The name of the icon to render.
 * @property {string} src - Provides the ability to set the SVG string content directly.
 * @property {boolean} [lazy=false] - Controls whether the icon will be loaded dynamically when it comes into view. False by default.
 * @property {boolean} [external=false] - Controls whether external network requests are allowed for this icon. Only pertains for icons that aren't already defined in the registry.
 * @property {IconExternalType} [externalType="standard"] - The type of icon to load externally. Possible values: "standard" (default), "extended", "custom".
 * @property {IconUrlBuilder} externalUrlBuilder - A callback that can be provided to generate a URL that will be used to fetch an SVG icon.
 * @property {string} viewbox - A custom value to apply to the `viewBox` attribute on the internal `<svg>` element.
 * @property {IconTheme} theme - The theme to apply to the icon.
 *
 * @attribute {string} name - The name of the icon to render.
 * @attribute {string} src - Provides the ability to set the SVG string content directly.
 * @attribute {boolean} [lazy=false] - Controls whether the icon will be loaded dynamically when it comes into view. False by default.
 * @attribute {boolean} [external=false] - Controls whether external network requests are allowed for this icon. Only pertains for icons that aren't already defined in the registry.
 * @attribute {IconExternalType} [externalType="standard"] - The type of icon to load externally. Possible values: "standard" (default), "extended", "custom".
 * @attribute {string} viewbox - A custom value to apply to the `viewBox` attribute on the internal `<svg>` element.
 * @attribute {IconTheme} theme - The theme to apply to the icon.
 *
 * @cssproperty --forge-icon-color - The color of the icon.
 * @cssproperty --forge-icon-size - The size of the icon. Defaults to the font-size of the context it is placed in.
 * @cssproperty --forge-icon-width - The width of the icon.
 * @cssproperty --forge-icon-height - The height of the icon.
 * @cssproperty --forge-icon-font-size - The font size of the icon.
 *
 * @csspart svg - The internal SVG element.
 */
@customElement({
  name: ICON_CONSTANTS.elementName
})
export class IconComponent extends BaseComponent implements IIconComponent {
  public static get observedAttributes(): string[] {
    return Object.values(ICON_CONSTANTS.observedAttributes);
  }

  private _core: IconCore;

  constructor() {
    super();
    attachShadowTemplate(this, template, styles);
    this._core = new IconCore(new IconAdapter(this));
  }

  public connectedCallback(): void {
    this._core.initialize();
  }

  public disconnectedCallback(): void {
    this._core.destroy();
  }

  public attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    switch (name) {
      case ICON_CONSTANTS.observedAttributes.NAME:
        this.name = newValue;
        break;
      case ICON_CONSTANTS.observedAttributes.SRC:
        this.src = newValue;
        break;
      case ICON_CONSTANTS.observedAttributes.LAZY:
        this.lazy = coerceBoolean(newValue);
        break;
      case ICON_CONSTANTS.observedAttributes.EXTERNAL:
        this.external = coerceBoolean(newValue);
        break;
      case ICON_CONSTANTS.observedAttributes.EXTERNAL_TYPE:
        this.externalType = newValue as IconExternalType;
        break;
      case ICON_CONSTANTS.observedAttributes.VIEWBOX:
        this.viewbox = newValue;
        break;
      case ICON_CONSTANTS.observedAttributes.THEME:
        this.theme = newValue as IconTheme;
        break;
    }
  }

  /** The name of the icon within the icon registry to be used. */
  @coreProperty()
  public declare name: string | undefined;

  /** Provides the ability to set the SVG string content directly. */
  @coreProperty()
  public declare src: string | undefined;

  /** Controls whether the icon will be loaded dynamically when it comes into view. False by default. */
  @coreProperty()
  public declare lazy: boolean;

  /** Controls whether external network requests are allowed for this icon. Only pertains for icons that aren't defined in the registry. */
  @coreProperty()
  public declare external: boolean;

  /** The type of icon to load externally. Possible values: "standard", "extended", "custom". */
  @coreProperty()
  public declare externalType: IconExternalType;

  /** A callback that can be provided to generate a URL that will be used to fetch an SVG icon. */
  @coreProperty()
  public declare externalUrlBuilder: IconUrlBuilder;

  /** A custom value to apply to the `viewBox` attribute on the internal `<svg>` element. */
  @coreProperty()
  public declare viewbox: string;

  @coreProperty()
  public declare theme: IconTheme;

  /** Forces a reload of the icon. */
  public layout(): void {
    this._core.layout();
  }
}
